import type { FC, MouseEvent, KeyboardEvent } from 'react';
import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { PositionType } from '@atlaskit/tooltip/types';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import { useShortcutStrings } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import {
	STAR_BUTTON_SHORTCUT,
	UNIVERSAL_STAR_BUTTON_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { useIsEditorOpen } from '@confluence/page-utils/entry-points/useIsEditorOpen.ts';
import type { ShortcutEvent } from '@confluence/shortcuts/entry-points/ShortcutListener.ts';

import {
	ConditionalHiddenAction,
	ConditionalSubtleHoverButton,
} from '../PresentationalComponents.tsx';
import { StarIcon } from '../StarIcon.tsx';

import type { PageStarProps } from './PageStar.tsx';
import { PageStar } from './PageStar.tsx';

export type ButtonProps = {
	size?: 'small' | 'medium';
	outlineAppearance?: 'normal' | 'subtle';
	tooltipPosition?: PositionType;
	shortcut?: string;
	label?: string;
	labelledBy?: string;
	id?: string;
	isDisabled?: boolean;
};

export const PageStarButton: FC<ButtonProps & PageStarProps> = ({
	size,
	outlineAppearance,
	tooltipPosition,
	shortcut,
	label,
	labelledBy,
	id,
	...props
}) => {
	const intl = useIntl();
	const isEditorOpen = useIsEditorOpen();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const spacing = size === 'small' ? 'compact' : 'default';
	const tooltipLabel = props.isStarred
		? intl.formatMessage(i18n.unstarContent)
		: intl.formatMessage(i18n.starContent);

	const shortcutStrings = useShortcutStrings({ shortcut });
	const buttonLabel = props.isStarred
		? intl.formatMessage(i18n.unstarContent)
		: intl.formatMessage(i18n.starContent);

	const handleClick = useCallback(
		(starHandler, triggeredByKeyboardShortcut) =>
			(e: MouseEvent | KeyboardEvent | ShortcutEvent) => {
				starHandler(e);
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'pageStar',
						attributes: {
							triggeredByKeyboardShortcut,
							action: props.isStarred ? 'unstar' : 'star',
						},
						source: 'PageStarButton',
					},
				}).fire();
			},
		[createAnalyticsEvent, props.isStarred],
	);

	return (
		<PageStar {...props}>
			{({ toggle: toggleStar }) => (
				<>
					<Tooltip
						content={tooltipLabel}
						shortcut={shortcutStrings}
						position={tooltipPosition || 'top'}
						hideTooltipOnClick
					>
						<ConditionalHiddenAction isHidden={!props.isStarred}>
							<ConditionalSubtleHoverButton
								id={id}
								isSubtle={outlineAppearance === 'subtle'}
								data-id={props.isStarred ? 'page-unstar-button' : 'page-star-button'}
								testId={props.isStarred ? 'page-unstar-button' : 'page-star-button'}
								spacing={spacing}
								appearance="subtle"
								onClick={handleClick(toggleStar, false)}
								iconBefore={<StarIcon isStarred={props.isStarred} spacing={props.spacing} />}
								aria-label={buttonLabel}
								aria-labelledby={labelledBy}
								aria-pressed={props.isStarred}
								isDisabled={props.isDisabled}
							/>
						</ConditionalHiddenAction>
					</Tooltip>
					{shortcut && (
						<GeneralShortcutListener
							deprecatedAccelerator={!isEditorOpen ? STAR_BUTTON_SHORTCUT : undefined}
							accelerator={UNIVERSAL_STAR_BUTTON_SHORTCUT}
							listener={handleClick(toggleStar, true)}
						/>
					)}
				</>
			)}
		</PageStar>
	);
};

const i18n = defineMessages({
	starContent: {
		id: 'action-buttons.content.starContent.label',
		defaultMessage: 'Star this content',
		description:
			'Label for unfilled star icon button which indicates that the content is unstarred & stars the content when clicked',
	},
	unstarContent: {
		id: 'action-buttons.content.unstarContent.label',
		defaultMessage: 'Unstar this content',
		description:
			'Label for filled star icon button which indicates that the content is starred & unstars the content when clicked',
	},
});
